import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Plus,
  Edit,
  Trash2,
  User,
  Clock,
  Scissors,
  Palette,
  Calendar,
  Briefcase,
  Palmtree,
  X
} from "lucide-react";

const MANSIONI_PRESET = [
  "Taglio Uomo",
  "Taglio Donna", 
  "Colorista",
  "Trattamenti",
  "Piega",
  "Barba",
  "Extension",
  "Acconciature",
  "Reception"
];

const GIORNI = [
  { key: "lunedi", label: "Lunedì" },
  { key: "martedi", label: "Martedì" },
  { key: "mercoledi", label: "Mercoledì" },
  { key: "giovedi", label: "Giovedì" },
  { key: "venerdi", label: "Venerdì" },
  { key: "sabato", label: "Sabato" },
  { key: "domenica", label: "Domenica" },
];

const COLORI_PRESET = [
  "#ef4444", "#f97316", "#f59e0b", "#84cc16", 
  "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6",
  "#6366f1", "#8b5cf6", "#a855f7", "#ec4899"
];

export default function GestioneOperatori() {
  const { token } = useAuth();
  const [operatori, setOperatori] = useState([]);
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOperatore, setEditingOperatore] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    data_nascita: "",
    mansioni: [],
    colore: "#3b82f6",
    telefono: "",
    email: "",
    servizi_abilitati: [],
    turni: GIORNI.map(g => ({
      giorno: g.key,
      attivo: g.key !== 'domenica',
      inizio: "09:00",
      fine: "19:00",
      pausa_inizio: "13:00",
      pausa_fine: "14:00"
    })),
    ferie: []
  });

  useEffect(() => {
    fetchOperatori();
    fetchServizi();
  }, []);

  const fetchOperatori = async () => {
    try {
      const response = await axios.get(`${API}/operatori?solo_attivi=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperatori(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchServizi = async () => {
    try {
      const response = await axios.get(`${API}/servizi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServizi(response.data);
    } catch (error) {
    }
  };

  const resetForm = () => {
    setEditingOperatore(null);
    setFormData({
      nome: "",
      cognome: "",
      data_nascita: "",
      mansioni: [],
      colore: "#3b82f6",
      telefono: "",
      email: "",
      servizi_abilitati: [],
      turni: GIORNI.map(g => ({
        giorno: g.key,
        attivo: g.key !== 'domenica',
        inizio: "09:00",
        fine: "19:00",
        pausa_inizio: "13:00",
        pausa_fine: "14:00"
      })),
      ferie: []
    });
  };

  const openEditDialog = (operatore) => {
    setEditingOperatore(operatore);
    setFormData({
      nome: operatore.nome,
      cognome: operatore.cognome || "",
      data_nascita: operatore.data_nascita || "",
      mansioni: operatore.mansioni || [],
      colore: operatore.colore || "#3b82f6",
      telefono: operatore.telefono || "",
      email: operatore.email || "",
      servizi_abilitati: operatore.servizi_abilitati || [],
      turni: operatore.turni || GIORNI.map(g => ({
        giorno: g.key,
        attivo: g.key !== 'domenica',
        inizio: "09:00",
        fine: "19:00",
        pausa_inizio: "13:00",
        pausa_fine: "14:00"
      })),
      ferie: operatore.ferie || []
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingOperatore) {
        await axios.put(`${API}/operatori/${editingOperatore.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Operatore aggiornato!");
      } else {
        await axios.post(`${API}/operatori`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Operatore creato!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchOperatori();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Disattivare questo operatore?")) return;
    
    try {
      await axios.delete(`${API}/operatori/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Operatore disattivato");
      fetchOperatori();
    } catch (error) {
      toast.error("Errore nella disattivazione");
    }
  };

  const toggleServizio = (servizioId) => {
    setFormData(prev => ({
      ...prev,
      servizi_abilitati: prev.servizi_abilitati.includes(servizioId)
        ? prev.servizi_abilitati.filter(id => id !== servizioId)
        : [...prev.servizi_abilitati, servizioId]
    }));
  };

  const toggleMansione = (mansione) => {
    setFormData(prev => ({
      ...prev,
      mansioni: prev.mansioni.includes(mansione)
        ? prev.mansioni.filter(m => m !== mansione)
        : [...prev.mansioni, mansione]
    }));
  };

  const updateTurno = (giornoKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      turni: prev.turni.map(t => 
        t.giorno === giornoKey ? { ...t, [field]: value } : t
      )
    }));
  };

  // Gestione Ferie
  const addFeria = () => {
    setFormData(prev => ({
      ...prev,
      ferie: [...prev.ferie, { 
        id: Date.now().toString(),
        data: "", 
        data_fine: "",
        motivo: "",
        tipo: "singolo" // singolo o range
      }]
    }));
  };

  const updateFeria = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ferie: prev.ferie.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const removeFeria = (index) => {
    setFormData(prev => ({
      ...prev,
      ferie: prev.ferie.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Gestione Operatori</h3>
          <p className="text-sm text-slate-500">Configura gli operatori del salone, turni e servizi</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="gap-2 bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nuovo Operatore
        </Button>
      </div>

      {/* Lista Operatori */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {operatori.map(op => (
          <Card 
            key={op.id} 
            className={`border-l-4 ${!op.attivo ? 'opacity-50' : ''}`}
            style={{ borderLeftColor: op.colore }}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: op.colore }}
                  >
                    {op.nome.charAt(0)}{(op.cognome || '').charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{op.nome} {op.cognome}</h4>
                    <p className="text-sm text-slate-500">{op.telefono || 'No telefono'}</p>
                    {op.data_nascita && (
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(op.data_nascita).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                      </p>
                    )}
                    {!op.attivo && (
                      <span className="text-xs text-red-500 font-medium">Disattivato</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(op)}>
                    <Edit className="w-4 h-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(op.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
              
              {/* Mansioni */}
              {op.mansioni && op.mansioni.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {op.mansioni.slice(0, 3).map(m => (
                    <span key={m} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                      {m}
                    </span>
                  ))}
                  {op.mansioni.length > 3 && (
                    <span className="px-2 py-0.5 bg-purple-200 text-purple-700 rounded text-xs">
                      +{op.mansioni.length - 3}
                    </span>
                  )}
                </div>
              )}
              
              {/* Servizi */}
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-slate-500 mb-2">Servizi abilitati:</p>
                <div className="flex flex-wrap gap-1">
                  {op.servizi_abilitati && op.servizi_abilitati.length > 0 ? (
                    servizi
                      .filter(s => op.servizi_abilitati.includes(s.id))
                      .slice(0, 4)
                      .map(s => (
                        <span key={s.id} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                          {s.nome}
                        </span>
                      ))
                  ) : (
                    <span className="text-xs text-slate-400">Tutti i servizi</span>
                  )}
                  {op.servizi_abilitati && op.servizi_abilitati.length > 4 && (
                    <span className="px-2 py-0.5 bg-slate-200 rounded text-xs text-slate-600">
                      +{op.servizi_abilitati.length - 4}
                    </span>
                  )}
                </div>
              </div>

              {/* Turni rapidi */}
              <div className="mt-2 flex gap-1">
                {GIORNI.map(g => {
                  const turno = (op.turni || []).find(t => t.giorno === g.key);
                  return (
                    <div 
                      key={g.key}
                      className={`w-6 h-6 rounded text-xs flex items-center justify-center font-medium ${
                        turno?.attivo 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-slate-100 text-slate-400'
                      }`}
                      title={`${g.label}: ${turno?.attivo ? `${turno.inizio}-${turno.fine}` : 'Riposo'}`}
                    >
                      {g.label.charAt(0)}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {operatori.length === 0 && (
          <div className="col-span-full text-center py-8 text-slate-500">
            <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessun operatore configurato</p>
            <p className="text-sm">Aggiungi il primo operatore per gestire gli appuntamenti</p>
          </div>
        )}
      </div>

      {/* Dialog Operatore */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingOperatore ? "Modifica Operatore" : "Nuovo Operatore"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dati Base */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Cognome</Label>
                <Input
                  value={formData.cognome}
                  onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Data Nascita
                </Label>
                <Input
                  type="date"
                  value={formData.data_nascita}
                  onChange={(e) => setFormData({ ...formData, data_nascita: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            {/* Mansioni */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Mansioni / Competenze
              </Label>
              <div className="flex flex-wrap gap-2">
                {MANSIONI_PRESET.map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMansione(m)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      formData.mansioni.includes(m)
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              {formData.mansioni.length > 0 && (
                <p className="text-xs text-slate-500">
                  Selezionate: {formData.mansioni.join(', ')}
                </p>
              )}
            </div>

            {/* Colore */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Colore Identificativo
              </Label>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg border-2 border-slate-300"
                  style={{ backgroundColor: formData.colore }}
                />
                <div className="flex flex-wrap gap-2">
                  {COLORI_PRESET.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`w-8 h-8 rounded-lg transition-transform ${
                        formData.colore === c ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : ''
                      }`}
                      style={{ backgroundColor: c }}
                      onClick={() => setFormData({ ...formData, colore: c })}
                    />
                  ))}
                </div>
                <Input
                  type="color"
                  value={formData.colore}
                  onChange={(e) => setFormData({ ...formData, colore: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
              </div>
            </div>

            {/* Servizi Abilitati */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Scissors className="w-4 h-4" />
                Servizi Abilitati
              </Label>
              <p className="text-xs text-slate-500">Seleziona i servizi che questo operatore può eseguire. Se nessuno è selezionato, può fare tutti i servizi.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                {servizi.map(s => (
                  <label 
                    key={s.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      formData.servizi_abilitati.includes(s.id)
                        ? 'bg-blue-100 border border-blue-300'
                        : 'bg-white border border-slate-200 hover:border-blue-200'
                    }`}
                  >
                    <Checkbox
                      checked={formData.servizi_abilitati.includes(s.id)}
                      onCheckedChange={() => toggleServizio(s.id)}
                    />
                    <span className="text-sm truncate">{s.nome}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Turni */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Turni Settimanali
              </Label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {GIORNI.map(g => {
                  const turno = formData.turni.find(t => t.giorno === g.key) || {};
                  return (
                    <div 
                      key={g.key}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        turno.attivo ? 'bg-emerald-50' : 'bg-slate-50'
                      }`}
                    >
                      <div className="w-24 flex items-center gap-2">
                        <Switch
                          checked={turno.attivo}
                          onCheckedChange={(checked) => updateTurno(g.key, 'attivo', checked)}
                        />
                        <span className="text-sm font-medium">{g.label}</span>
                      </div>
                      
                      {turno.attivo && (
                        <>
                          <div className="flex items-center gap-1">
                            <Input
                              type="time"
                              value={turno.inizio || "09:00"}
                              onChange={(e) => updateTurno(g.key, 'inizio', e.target.value)}
                              className="w-24 h-8 text-sm"
                            />
                            <span className="text-slate-400">-</span>
                            <Input
                              type="time"
                              value={turno.fine || "19:00"}
                              onChange={(e) => updateTurno(g.key, 'fine', e.target.value)}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <span>Pausa:</span>
                            <Input
                              type="time"
                              value={turno.pausa_inizio || ""}
                              onChange={(e) => updateTurno(g.key, 'pausa_inizio', e.target.value || null)}
                              className="w-20 h-7 text-xs"
                              placeholder="--:--"
                            />
                            <span>-</span>
                            <Input
                              type="time"
                              value={turno.pausa_fine || ""}
                              onChange={(e) => updateTurno(g.key, 'pausa_fine', e.target.value || null)}
                              className="w-20 h-7 text-xs"
                              placeholder="--:--"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ferie e Chiusure Straordinarie */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Palmtree className="w-4 h-4" />
                  Ferie e Chiusure Straordinarie
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addFeria}
                >
                  <Plus className="w-3 h-3 mr-1" /> Aggiungi
                </Button>
              </div>
              
              {formData.ferie.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4 bg-slate-50 rounded-lg">
                  Nessuna ferie o chiusura programmata
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.ferie.map((feria, index) => (
                    <div 
                      key={feria.id || index}
                      className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200"
                    >
                      <select
                        value={feria.tipo || "singolo"}
                        onChange={(e) => updateFeria(index, "tipo", e.target.value)}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="singolo">Giorno singolo</option>
                        <option value="range">Periodo</option>
                      </select>
                      
                      <Input
                        type="date"
                        value={feria.data || ""}
                        onChange={(e) => updateFeria(index, "data", e.target.value)}
                        className="w-36 h-8 text-sm"
                        placeholder="Data inizio"
                      />
                      
                      {feria.tipo === "range" && (
                        <>
                          <span className="text-slate-400">→</span>
                          <Input
                            type="date"
                            value={feria.data_fine || ""}
                            onChange={(e) => updateFeria(index, "data_fine", e.target.value)}
                            className="w-36 h-8 text-sm"
                            placeholder="Data fine"
                          />
                        </>
                      )}
                      
                      <Input
                        type="text"
                        value={feria.motivo || ""}
                        onChange={(e) => updateFeria(index, "motivo", e.target.value)}
                        className="flex-1 h-8 text-sm"
                        placeholder="Motivo (es. Ferie, Permesso...)"
                      />
                      
                      <Button 
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeria(index)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Aggiungi ferie, permessi o giorni di chiusura specifici per questo operatore
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Annulla
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                {editingOperatore ? "Salva Modifiche" : "Crea Operatore"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
